<template>
  <div>
    <!-- The invoice id -->
    <div class="minorTitle">
      Invoice #{{ currentInvoice.invoice_id }} &middot; {{ currentInvoice.invoice_display_creation }} &middot;
      <span
        v-if="!receiptIsLoading"
        class="navLink"
        @click="printReceipt()"
      ><i class="fa fa-print" /> Print</span><div
        v-else
        class="inlineLoadingContainer"
      >
        <span class="loading">Loading</span>
      </div>
      <span v-if="trackingInfo !== undefined">
        &middot;
        <span
          v-show="!trackingInfoIsLoading"
          class="navLink"
          @click="trackShipment"
        >Track Shipment</span>
        <div
          v-if="trackingInfoIsLoading"
          class="inlineLoadingContainer"
          style="margin-left: 5rem;"
        ><span class="loading">Loading</span></div>
      </span>
      <br>
      <span
        v-if="currentInvoice.payment_status === 'paid'"
        class="colorSuccess"
      >
        This invoice has been paid in full.
      </span>
      <span
        v-else-if="currentInvoice.payment_status === 'overpaid'"
        class="colorSuccess"
      >
        Credit Owed: {{ currentInvoice.display_invoice_balance }}
      </span>
      <span
        v-else
        class="colorError"
      >
        Amount Due: {{ currentInvoice.display_invoice_balance }}
      </span>
    </div>

    <!-- The invoice -->
    <div class="accountMenuFullHeightContainer">
      <!-- Charges section -->
      <div
        v-if="currentInvoice.invoice_items.length > 0"
        class="marginNoneBottom"
      >
        <div class="minorTitle">
          Charges
        </div>

        <!-- Loop through the items on the invoice -->
        <ul>
          <li
            v-for="item in currentInvoice.invoice_items"
            :key="item.id"
            class="itemRow"
          >
            <a
              class="colorBlack navLink u-full-width"
              :href="item.url"
            >
              <div class="dropdownLeadImageContainer">
                <img
                  v-if="item.image !== null"
                  :src="item.image"
                  :alt="item.lead"
                >
              </div>
              <div class="flexBox flexGrow flexBoxSpread">
                <div>
                  <span v-text="item.description" />
                </div>
                <div>
                  <span v-text="item.display_hammer_price" />
                </div>
              </div>
            </a>
          </li>
        </ul>

        <!-- Totals container -->
        <div class="row">
          <div class="twelve columns colorGrey paddingSmallRight">
            <div
              v-if="currentInvoice.invoice_buyers_total > 0"
              class="row"
            >
              <div class="nine columns textRight">
                <span class="flexGrow">Buyer's Premium</span>
              </div>
              <div class="three columns textRight">
                <span v-text="currentInvoice.display_invoice_buyers_total" />
              </div>
            </div>
            <div
              v-if="currentInvoice.invoice_tax_total > 0"
              class="row colorGrey"
            >
              <div class="nine columns textRight">
                <span class="flexGrow">Sales Tax</span>
              </div>
              <div class="three columns textRight">
                <span v-text="currentInvoice.display_invoice_tax_total" />
              </div>
            </div>
            <div class="row">
              <div class="nine columns textRight colorGrey">
                <span class="flexGrow">Total Charges</span>
              </div>
              <div class="three columns textRight">
                <span v-text="currentInvoice.display_invoice_total" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment section -->
      <div v-if="Object.keys(currentInvoice.invoice_payments).length > 0">
        <div class="minorTitle">
          Payments
        </div>

        <!-- Loop through the payments on the invoice -->
        <ul>
          <li
            v-for="payment in currentInvoice.invoice_payments"
            :key="payment.invoice_payment_id"
            class="itemRow"
          >
            <div>
              <span v-html="payment.display_payment" />
            </div>
            <div>
              <span v-text="payment.display_amount" />
            </div>
          </li>
        </ul>

        <!-- Payment totals container -->
        <div class="row">
          <div class="twelve columns colorGrey paddingSmallRight">
            <div class="row">
              <div class="nine columns textRight colorGrey">
                <span class="paddingSmallRight flexGrow">Total Payments</span>
              </div>
              <div class="three columns textRight">
                <span v-text="currentInvoice.display_payment_total" />
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="twelve columns paddingSmallRight">
            <div class="row marginNormalTop">
              <div class="nine columns textRight colorGrey">
                <span class="paddingSmallRight flexGrow">{{ balanceLabel }}</span>
              </div>
              <div class="three columns textRight fontBold">
                <span v-text="currentInvoice.display_invoice_balance" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment button -->
      <PayInvoice
        v-if="currentInvoice.payment_status === 'unpaid'"
        :invoice_id="currentInvoice.invoice_id"
        :amount="currentInvoice.invoice_balance"
        class="paddingLargeTop"
      />

      <!-- Viewing the options of a paid invoice -->
      <div
        v-else-if="currentInvoice.payment_status === 'paid'"
        class="colorGrey paddingLargeBottom"
      >
        <!-- They have another unpaid invoice -->
        <div v-if="hasUnpaidInvoice">
          There are additional unpaid invoices for your account.  All invoices must be paid in full before shipping or pick-up arrangements can be made.
          <button
            class="bidButton backgroundColorDefault marginLargeTop"
            @click="insertAccountMenu('Invoices')"
          >
            Review unpaid Invoices
          </button>
        </div>
      </div>
    </div>

    <AlertComponent
      v-show="error.show"
      :title="error.title"
      :message="error.message"
      @close="closeError()"
    />
    <LoadingOverlayComponent
      v-if="showLoading"
      :title="loadingTitle"
    />
  </div>
</template>

<script>
import PayInvoice from '@/components/Forms/PayInvoice'
import LoadingOverlayComponent from '@/components/Shared/LoadingOverlayComponent.vue'
import AlertComponent from '../Shared/AlertComponent.vue'
import { get } from 'lodash'

export default {
  name: 'InvoiceCard',
  components: {
    PayInvoice,
    LoadingOverlayComponent,
    AlertComponent
  },
  /**
   * Component state
   */
  data () {
    return {
      loadingTitle: 'Gathering your invoice details',
      receiptIsLoading: false,
      receiptHtml: null,
      trackingInfoIsLoading: false,
      error: {
        show: false,
        title: '',
        message: ''
      }
    }
  },
  computed: {
    /**
     * Computed property: The api response object
     * @public
     */
    apiResponse: function () {
      return this.$store.getters.apiResponse
    },
    /**
     * Computed property: The balance label
     * @public
     */
    balanceLabel: function () {
      return (this.currentInvoice.payment_status === 'overpaid') ? 'Credit Owed' : 'Amount Due'
    },
    /**
     * Computed property: Whether the invoice is loading
     * @public
     */
    showLoading: function () {
      return (typeof this.apiResponse.get_customer_invoice === 'undefined')
    },
    /**
     * Computed property: The current customer object
     * @public
     */
    customer: function () {
      return this.$store.getters.customer
    },
    /**
     * Computed property: The current invoice object
     * @public
     */
    currentInvoice: function () {
      return this.$store.getters.invoices['INVOICEID_' + this.$store.getters.currentInvoice]
    },
    /**
     * Computed property: Whether customer has unpaid invoice
     * @public
     */
    hasUnpaidInvoice: function () {
      return this.$store.getters.hasUnpaidInvoice
    },
    /**
     * Computed property: The inventory ids on the shipping request quote
     * @public
     */
    requestQuoteInventoryIds: function () {
      return this.$store.getters.requestQuoteInventoryIds
    },
    /**
     * Computed property: Whether invoice has tracking info
     * @returns arta_shipment_id|undefined
     * @public
     */
    trackingInfo: function () {
      // In order to view tracking, invoice needs to have a shipping charge line item, paid in full, and a valid arta_shipment_id
      if (this.currentInvoice.invoice_items.find(x => x.shipping_charge === 'y') && this.currentInvoice.paid_in_full === 'y') {
        const shipment = this.currentInvoice.invoice_items.reduce((acc, curr) => {
          if (curr.arta_shipment_id !== null && curr.arta_shipment_id !== undefined) {
            acc.push(curr)
          }
          return acc
        }, [])

        if (shipment !== undefined && shipment.length > 0) {
          return shipment[0].arta_shipment_id
        }
      }
      return undefined
    }
  },
  /**
   * Gets called when the component mounts
   */
  mounted () {
    // We don't have the invoice, get it
    this.$store.dispatch('getCustomerInvoice', {invoiceId: this.$store.getters.currentInvoice})
  },
  methods: {
    /**
     * Gets called when user closes AlertComponent
     * @public
     */
    closeError: function () {
      this.error = {
        show: false,
        title: '',
        message: ''
      }
    },
    /**
     * Gets called when user clicks "Review unpaid Invoices" button
     * @param {*} payload
     */
    insertAccountMenu (payload) {
      this.$store.dispatch('insertAccountHistory', payload)
    },
    /**
     * Gets called when user clicks Print link
     * @public
     */
    printReceipt: function () {
      if (this.receiptHtml) {
        return this.openReceipt()
      }
      this.receiptIsLoading = true
      this.$store.dispatch('getCustomerInvoiceReceipt', {invoiceId: this.currentInvoice.invoice_id})
        .then(response => {
          this.receiptIsLoading = false
          this.receiptHtml = response.data
          this.openReceipt()
        })
    },
    /**
     * Gets called by printReceipt()
     */
    openReceipt: function () {
      const divElementContents = this.receiptHtml
      const receiptWindow = window.open('', '', 'width=850, height=1100')
      receiptWindow.document.write(divElementContents)
      receiptWindow.document.close()
    },
    /**
     * Gets called when user clicks "Track Shipment" link
     * @public
     */
    trackShipment: function () {
      this.trackingInfoIsLoading = true
      this.$store.dispatch('getArtaShippingStatusForInvoice', this.trackingInfo)
        .then(response => {
          this.trackingInfoIsLoading = false
          const url = get(response, 'data.payload.shipment.response.url')
          if (url) {
            return window.open(url, '_blank')
          }
          // Handle error, why no url?
          this.error = {
            show: true,
            title: '',
            message: 'Unable to locate tracking url.'
          }
        })
    }
  }
}

</script>

<style scoped>

</style>
